import React, {useEffect, useMemo, useState} from 'react';
import {useDispatch} from 'react-redux';
import {Space, Tabs} from 'antd';
import {useLocation, useNavigate} from 'react-router-dom';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import SpinSmall from '../../../SpinSmall';
import InvoiceDetails from '../InvoiceDetails';
import InvoiceNavigationButtons from '../InvoiceNavigationButtons';
import TransactionMatchEditor from '../TransactionMatchEditor';
import AuthenticationWindow from '../../CardsPage/AuthenticationWindow';
import RequestPaymentModal from '../RequestPaymentModal';
import InvoicePaymentDetails from '../InvoicePaymentDetails';
import BudgetEditor from '../BudgetEditor';
import {
  invoicesHelpers,
  objectHelpers,
  scaHelpers,
  systemHelpers,
  transactionsHelpers
} from '../../../../utils/helpers';
import { firebaseEvents } from '../../../../snippets/firebase';
import {
  companyFeaturesConstants, DEFAULT_MODAL_PROPS, invoiceApprovalStatusConstants,
  invoicePaymentStatusConstants,
  invoiceStatusConstants,
  SCAActionsConstants
} from '../../../../constants';
import { invoicesActions } from '../../../../state/actions/invoice.actions';
import { transactionActions } from '../../../../state/actions/transaction.actions';
import {useFeatureAvailable, useIsTreezorEnabled} from '../../../../hooks';

const transactionAuthActionName = SCAActionsConstants.TRANSACTIONS_WITHIN_90_DAYS_LOADING_ACTION;

const transactionActionsNames = {
  LOAD_LIST: 'LOAD_LIST',
  LOAD_MATCHED: 'LOAD_MATCHED'
};

const panelTypes = {
  MATCHING: 'matching',
  PAYMENT: 'payment',
};

const tabKeys = {
  DETAILS_TAB: 'details',
  PAYMENT_TAB: 'payment',
};

const { MATCHING, PAYMENT } = panelTypes;
const { LOAD_LIST, LOAD_MATCHED } = transactionActionsNames;
const { DETAILS_TAB, PAYMENT_TAB } = tabKeys;

const InvoiceOverview = ({
  approvals = [],
  invoice,
  neighborIds,
  onEdit,
  onSelectedTransactionChange,
  onTransactionsChange,
  onUpdate,
  selectedTransaction,
  transactions,
  ...rest
}) => {
  const [activeAction, setActiveAction] = useState(false);
  const [activePanelType, setActivePanelType] = useState(MATCHING);
  const [activeTabKey, setActiveTabKey] = useState(DETAILS_TAB);
  const [authWindowProps, setAuthWindowProps] = useState({ open: false });
  const [isAvailableSuggested, setIsAvailableSuggested] = useState(true);
  const [isDefinedTransaction, setIsDefinedTransaction] = useState(false);
  const [loading, setLoading] = useState(false);
  const [transactionsQuery, setTransactionsQuery] = useState({});
  const [matchedTransaction, setMatchedTransaction] = useState(undefined);
  const [paymentRequestModalProps, setPaymentRequestModalProps] = useState(DEFAULT_MODAL_PROPS);

  const isAvailablePaymentTab = useMemo(() => {
    return [invoiceApprovalStatusConstants.APPROVED, invoiceApprovalStatusConstants.REJECTED, invoiceApprovalStatusConstants.PENDING].includes(invoice?.approval_status);
  }, [invoice?.approval_status]);

  const [t] = useTranslation('main');
  const location = useLocation();
  const navigate = useNavigate();

  const dispatch = useDispatch();

  const isTreezorEnabled = useIsTreezorEnabled();

  const isBudgetsAvailable = useFeatureAvailable(companyFeaturesConstants.BUDGETS_PO_AVAILABLE);

  const isReadyToMatch = useMemo(
    () => selectedTransaction?.id || selectedTransaction.external,
    [selectedTransaction]
  );

  const isLatestRequestForInvoiceExpenses = useMemo(
    () => transactionsQuery.hasOwnProperty('transaction_id_in'),
    [transactionsQuery]
  );

  const payButtonProps = useMemo(
    () => ({ available: activePanelType === MATCHING && !isDefinedTransaction && !selectedTransaction.external && isTreezorEnabled }),
    [activePanelType, isDefinedTransaction, selectedTransaction?.external, isTreezorEnabled]
  );

  const paymentRequestButtonProps = useMemo(() => ({
    available: invoicesHelpers.isUnassignedInvoice(invoice) && isBudgetsAvailable,
    disabled: !Boolean(invoice?.budget_request_id)
  }), [invoice?.approval_status, invoice?.budget_request_id, isBudgetsAvailable]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync active tab with URL hash (e.g. #details) on mount and when location changes
  useEffect(() => {
    const hash = (location && location.hash) ? location.hash.replace('#', '') : window.location.hash.replace('#', '');
    if (hash === DETAILS_TAB || hash === PAYMENT_TAB) {
      setActiveTabKey(hash);
    }
  }, [location]);

  // Load invoice expenses and set transaction state
  useEffect(() => {
    if (!invoice || !isTreezorEnabled) return;
    const { id } = invoice;
    const isMatched = invoice?.status === invoiceStatusConstants.MATCHED;
    if (isMatched !== isDefinedTransaction) setIsDefinedTransaction(isMatched);
    loadInvoiceExpenses({ id, isMatched, initial: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice, isTreezorEnabled]);

  const startLoading = () => setLoading(true);

  const finishLoading = () => setLoading(false);

  const finishTransactionsLoading = (data = [], type = 'searched') => {
    onTransactionsChange({...transactions, [type]: data});
    finishLoading();
  }

  const finishMatchedTransactionsLoading = (transactions = []) => {
    setMatchedTransaction(transactions?.[0]);
    finishLoading();
  }

  const loadTransactions = ({isMatched = false, type = 'searched', query} = {}) => {
    if (!loading) startLoading();

    const actionName = isMatched ? LOAD_MATCHED : LOAD_LIST;

    if (activeAction !== actionName) setActiveAction(actionName);

    query = {
      amount_gte: 0.01,
      ...query,
      is_incoming: false,
      payment_status_not_in: transactionsHelpers.getDeclinedTransactionStatuses(),
      sort_order: 'desc'
    }

    dispatch(transactionActions.getTransactionsList({
      headers: scaHelpers.getAuthHeaders(transactionAuthActionName),
      query,
      successCallback: (data) => isMatched ? finishMatchedTransactionsLoading(data) : finishTransactionsLoading(data, type),
      errorCallback: (response) => {
        scaHelpers.SCAResponseCallback({
          response,
          scaCallback: (scaProps) => setAuthWindowProps({...authWindowProps, ...scaProps}),
          errorCallback: () => isMatched ? finishMatchedTransactionsLoading() : finishTransactionsLoading([], type)
        });
      }
    }));
  }

  const loadInvoiceExpenses = ({id, initial = false, isMatched = false}) => {
    if (!isMatched) {
      startLoading();

      dispatch(invoicesActions.getInvoiceRelatedExpenses({
        id,
        successCallback: (data) => {
          const transactionIds = data.map(e => e.card_transaction_id).filter(Boolean);

          if (!data.length || !transactionIds.length) {
            if (initial) {
              setIsAvailableSuggested(false);
              setLoading(false);
            } else {
              finishLoading();
              if (transactions.suggested.length) onTransactionsChange({...transactions, suggested: []});
            }
            return;
          }

          const query = {transaction_id_in: transactionIds};
          setTransactionsQuery(query);
          loadTransactions({type: 'suggested', query});
        },
        errorCallback: () => finishTransactionsLoading([], 'suggested')
      }));
    } else {
      const transactionId = invoice?.expenses?.[0]?.transaction_id;
      if (!transactionId) return;

      const query = {transaction_id_in: [transactionId]};
      setTransactionsQuery(query);
      loadTransactions({isMatched: true, query});
    }
  }

  const handlePayNowClick = () => setActivePanelType(PAYMENT);

  const handleRequestPaymentClick = () => setPaymentRequestModalProps({...paymentRequestModalProps, open: true });

  const handleBackClick = () => setActivePanelType(MATCHING);

  const goToInvoice = (invoiceId) => {
    if (!invoiceId) return;

    navigate(invoicesHelpers.getInvoiceDetailsLink(invoiceId));
  }

  const handleMoveNext = () => goToInvoice(neighborIds.next);

  const handlePaidOutside = ({paid = true, successCallback} = {}) => {
    if (!invoice) return;

    dispatch(invoicesActions.payInvoiceOutside({
      id: invoice.id,
      paid,
      successCallback,
      errorCallback: finishLoading
    }));
  }

  const handleRemoveMatching = () => {
    const expense = invoice?.expenses?.[0];
    const expenseId = expense?.expense_id;
    if (expenseId) {
      dispatch(invoicesActions.unlinkExpense({ data: {expense_id: expenseId}, successCallback: onUpdate }));
    } else if (invoice.payment_status === invoicePaymentStatusConstants.PAID_OUTSIDE) {
      handlePaidOutside({ paid: false, successCallback: onUpdate });
    }
  }

  const handleOkEditModal = () => {
    handleRemoveMatching();
    loadInvoiceExpenses({id: invoice?.id});
    setIsDefinedTransaction(false);
    setActivePanelType(MATCHING);
  }

  const handleCloseAuthModal = () => {
    finishLoading();
    setAuthWindowProps({...authWindowProps, open: false});
  }

  const handleOnSuccessAuth = () => {
    const actions = {
      [LOAD_LIST]: () => loadTransactions({query: transactionsQuery}),
      [LOAD_MATCHED]: () => loadTransactions({isMatched: true, query: transactionsQuery}),
    };

    setAuthWindowProps({...authWindowProps, open: false});
    actions[activeAction]?.();
  }

  const handleLoadInvoiceExpenses = () => {
    if (!invoice || !invoice.id) return;

    const isMatched = invoice.status === invoiceStatusConstants.MATCHED;
    loadInvoiceExpenses({id: invoice.id, isMatched});
  }

  const handleFilterTransactions = (params) => {
    const baseQuery = invoicesHelpers.convertMatchFilterPropsToApi(params);
    const hasNoFilters = objectHelpers.allValuesEmpty(baseQuery);

    const query = {
      ...baseQuery,
      has_invoice: false
    };

    setTransactionsQuery(query);

    if (hasNoFilters) {
      if (!isLatestRequestForInvoiceExpenses) {
        handleLoadInvoiceExpenses();
      }
      return;
    }

    loadTransactions({query});
  };

  const handleCloseFilter = (closed = false) => {
    if (closed || isLatestRequestForInvoiceExpenses || isReadyToMatch) return;

    handleLoadInvoiceExpenses();
  };

  const handleSelectTransaction = ({type, value} = {}) => {
    const isExternal = type === 'external';
    onSelectedTransactionChange({
      id: isExternal ? undefined : value,
      external: isExternal ? value : false
    });
  }

  const handlePaymentSuccess = () => {
    onUpdate?.();
    finishLoading();
    systemHelpers.logEvent(firebaseEvents.INVOICES_EDIT_INVOICE_TRANSACTION);
  }

  const handleClosePaymentRequestModal = () => setPaymentRequestModalProps(DEFAULT_MODAL_PROPS);

  const handlePaymentRequestModalOk = ({data, successCallback, errorCallback}) => {
    setPaymentRequestModalProps({...paymentRequestModalProps, loading: true});

    dispatch(invoicesActions.requestInvoicePayment({
      id: invoice.id,
      data,
      successCallback: (response) => {
        setPaymentRequestModalProps(DEFAULT_MODAL_PROPS);
        successCallback?.(response);
        onUpdate?.(response);
        setActiveTabKey(PAYMENT_TAB);
      },
      errorCallback: (response) => {
        setPaymentRequestModalProps({...paymentRequestModalProps, loading: false});
        errorCallback?.(response);
      }
    }));
  }

  const handleOnChangeTab = (key) => navigate(`${location.pathname}#${key}`);

  const tabItems = [
    {
      label: (
        <Space size='small'>
          <span>{t('details')}</span>
          {invoicesHelpers.getInvoiceSourceTypeTag(invoice?.source_type)}
        </Space>
      ),
      key: DETAILS_TAB,
      children: (
        <Space direction='vertical' size='middle'>
          <InvoiceDetails
            invoice={invoice}
            isVendorEditDisabled={isDefinedTransaction}
            onEdit={onEdit}
          />
          {invoice && (
            <SpinSmall spinning={loading}>
              {isTreezorEnabled && (
                <TransactionMatchEditor
                  activePanelType={activePanelType}
                  isAvailableSuggested={isAvailableSuggested}
                  isDefinedTransaction={isDefinedTransaction}
                  invoice={invoice}
                  loading={loading}
                  matchedTransaction={matchedTransaction}
                  onCloseFilter={handleCloseFilter}
                  onEditModalOk={handleOkEditModal}
                  onFilter={handleFilterTransactions}
                  onNext={handleMoveNext}
                  onPaymentSuccess={handlePaymentSuccess}
                  onSelectTransaction={handleSelectTransaction}
                  transactions={transactions}
                />
              )}
              {isBudgetsAvailable && <BudgetEditor invoice={invoice} onUpdate={onUpdate} />}
            </SpinSmall>
          )}

        </Space>
      )
    },
    isAvailablePaymentTab && {
      label: (
        <Space size='small'>
          <span>{t('payment')}</span>
          {invoicesHelpers.getInvoiceApprovalStatusTag(invoice, t)}
        </Space>
      ),
      key: PAYMENT_TAB,
      children: <InvoicePaymentDetails approvals={approvals} invoice={invoice} onUpdate={onUpdate} />,
    }
  ].filter(Boolean);

  const tabBarExtraContent = (
    <InvoiceNavigationButtons
      backButtonProps={{
        available: activePanelType === PAYMENT,
        onClick: handleBackClick
      }}
      invoice={invoice}
      neighborIds={neighborIds}
      payButtonProps={{
        ...payButtonProps,
        onClick: handlePayNowClick,
      }}
      paymentRequestButtonProps={{
        ...paymentRequestButtonProps,
        onClick: handleRequestPaymentClick
      }}
    />
  );

  return (
    <Space direction='vertical' size='middle' {...rest}>

      <Tabs
        activeKey={activeTabKey}
        items={tabItems}
        onChange={handleOnChangeTab}
        tabBarExtraContent={tabBarExtraContent}
      />

      <RequestPaymentModal
        {...paymentRequestModalProps}
        handleCancel={handleClosePaymentRequestModal}
        handleOk={handlePaymentRequestModalOk}
        invoice={invoice}
      />

      <AuthenticationWindow
        {...authWindowProps}
        authModalProps={{ title: `${t('transactions')} ${t('loading')}` }}
        handleCancel={handleCloseAuthModal}
        onSuccess={handleOnSuccessAuth}
        operationName={transactionAuthActionName}
      />
    </Space>
  );
};

InvoiceOverview.propTypes = {
  approvals: PropTypes.arrayOf(
    PropTypes.shape({
      approval_date: PropTypes.string,
      id: PropTypes.string,
      is_approved: PropTypes.bool,
      name: PropTypes.string,
    })
  ),
  invoice: PropTypes.object,
  neighborIds: PropTypes.shape({
    next: PropTypes.string,
    prev: PropTypes.string
  }),
  onEdit: PropTypes.func,
  onSelectedTransactionChange: PropTypes.func,
  onTransactionsChange: PropTypes.func,
  onUpdate: PropTypes.func,
  selectedTransaction: PropTypes.shape({
    external: PropTypes.bool,
    id: PropTypes.string,
  }),
  transactions: PropTypes.shape({
    suggested: PropTypes.array,
    searched: PropTypes.array,
  }),
};

export default InvoiceOverview;
